import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Spinner,
  Stat,
  StatArrow,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PluginProps } from '../Plugin';
import useAssessmentContentRenderer from '../../../../app/pages/Unit/hooks/useAssessmentContentRenderer';
import Select from 'react-select';
import TeacherAssessment from '../../../../app/pages/Unit/TeacherAssessment';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { AddIcon, LinkIcon } from '@chakra-ui/icons';
import { axiosWrapper } from '../../../../../hooks/axios';
import Tippy from '@tippyjs/react';

interface Answer {
  data: string;
  isChanged?: boolean;
  pk?: any;
  grade?: number;
  comment?: string;
}

const AssessmentPluginBlock = (props: PluginProps) => {
  const {
    content,
    studyGroup,
    currentAssessmentQuestion: newCurrentAssessmentQuestion,
  } = props;
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string | number, Answer>>({});
  const user = useContext(AuthContext);
  const [displayUserPk, setDisplayUserPk] = useState(user?.pk);
  const isTeacher = user?.roles.includes(Role.teacher);
  const [gradingAnswer, setGradingAnswer] = useState<any>(null);
  const [maxPoints, setMaxPoints] = useState<any>(10);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sliderValue, setSliderValue] = useState(5);
  const [isGradeSaving, setIsGradeSaving] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [currentAssessmentQuestion, setCurrentAssessmentQuestion] =
    useState(-1);

  const {
    isLoading,
    isSaving,
    oldAnswers,
    studentTeams,
    saveAll,
    initAssessments,
  } = useAssessmentContentRenderer({
    displayUserPk,
    assessment: content.data,
    studyGroupPk: studyGroup.pk,
  });

  useEffect(() => {
    const newAnswers: any = {};
    oldAnswers.forEach((oldAnswer) => {
      newAnswers[oldAnswer.fields.question] = {
        pk: oldAnswer.pk,
        isChanged: false,
        data: oldAnswer.fields.answer,
        grade: oldAnswer.fields.grade,
        comment: oldAnswer.fields.comment,
      };
    });
    setAnswers({ ...newAnswers });
  }, [oldAnswers]);

  const hasChanges = Object.values(answers).reduce(
    (acc, cur) => acc || (cur.isChanged ? true : false),
    false,
  );

  useEffect(() => {
    if (newCurrentAssessmentQuestion >= 0) {
      if (hasChanges) {
        handleSaveAll().then(() => {
          setCurrentAssessmentQuestion(newCurrentAssessmentQuestion);
        });
      } else {
        setCurrentAssessmentQuestion(newCurrentAssessmentQuestion);
      }
    } else {
      setCurrentAssessmentQuestion(newCurrentAssessmentQuestion);
    }
  }, [newCurrentAssessmentQuestion]);

  const handleSaveAll = async () => {
    try {
      await saveAll(answers);
      Object.keys(answers).forEach((answerId) => {
        if (answers[answerId]) {
          answers[answerId]!.isChanged = false;
        }
      });
      setAnswers({ ...answers });
    } catch (e) {
      console.log(e);
    }
  };

  const grade = async () => {
    if (!gradingAnswer) {
      return;
    }
    setIsGradeSaving(true);
    axiosWrapper('/api/v1/syllabus/assessment_answers/', 'patch', {
      id: gradingAnswer,
      grade: sliderValue,
      comment: commentValue,
    })
      .then(() => {
        initAssessments();
        onClose();
      })
      .finally(() => {
        setIsGradeSaving(false);
      });
  };

  const totalPoints = content.data.questions.reduce(
    (acc: number, q: any) => acc + q.max_points,
    0,
  );
  const userPoints = content.data.questions.reduce((acc: number, q: any) => {
    const userAnswer = answers[q.pk];
    if (userAnswer) {
      const { grade } = userAnswer;
      if (grade && grade > 0) {
        acc += grade;
      }
    }
    return acc;
  }, 0);
  const isGraded = userPoints > 0;
  const solvedQuestions = content.data.questions.reduce(
    (acc: number, q: any) => {
      const userAnswer = answers[q.pk];
      return acc + (userAnswer ? 1 : 0);
    },
    0,
  );
  const percentage = totalPoints === 0 ? 1 : userPoints / totalPoints;
  const solvedPartially = solvedQuestions > 0;
  const solvedCompletely = solvedQuestions === content.data.questions.length;

  return (
    <Box marginBottom="80px">
      <Text fontSize="3xl">{content.data.title}</Text>
      {isTeacher && studentTeams && studyGroup && studyGroup.students && (
        <TeacherAssessment
          isGroupAssessment={content.data.isGroup}
          studentTeams={studentTeams || []}
          students={studyGroup.students}
          setDisplayUserPk={(userId) => setDisplayUserPk(userId)}
        />
      )}
      <StatGroup mt={5}>
        <Stat>
          <StatLabel>{t('plugin.assestment.points')}</StatLabel>
          <StatNumber>
            {isGraded
              ? userPoints
              : solvedCompletely
              ? t('plugin.assestment.solvedNotGraded')
              : solvedPartially
              ? t('plugin.assestment.solvedPartiallyNotGraded')
              : t('plugin.assestment.notSolved')}
          </StatNumber>
          {isGraded && (
            <StatHelpText>
              <StatArrow type={percentage > 0.5 ? 'increase' : 'decrease'} />
              {Math.round(percentage * 10000) / 100}%
            </StatHelpText>
          )}
        </Stat>
        <Stat>
          <StatLabel>{t('plugin.assestment.maxPoints')}</StatLabel>
          <StatNumber>{totalPoints}</StatNumber>
        </Stat>
      </StatGroup>
      <div
        style={{ marginTop: '20px' }}
        dangerouslySetInnerHTML={{ __html: content.data.description }}
      ></div>
      {isLoading ? (
        <Spinner />
      ) : (
        <Box>
          {content.data.questions.map((q: any, index: number) => {
            if (currentAssessmentQuestion !== -1) {
              if (currentAssessmentQuestion !== index) {
                return null;
              }
            }
            const grade = answers[q.pk]?.grade || -1;
            const comment = answers[q.pk]?.comment || '';
            return (
              <Box key={q.pk} paddingTop="40px">
                <HStack alignItems="baseline">
                  <Text fontSize="2xl" fontWeight="bold">
                    {index + 1}.{' '}
                  </Text>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: q.question,
                    }}
                  ></div>
                </HStack>
                <Textarea
                  isDisabled={isTeacher || grade > 0}
                  placeholder={t('plugin.assestment.writeYourAnswer')}
                  size="lg"
                  resize="vertical"
                  minHeight="200px"
                  value={answers[q.pk]?.data as string}
                  onChange={(e) => {
                    answers[q.pk] = {
                      ...answers[q.pk],
                      data: e.target.value,
                      isChanged: true,
                    };
                    setAnswers({ ...answers });
                  }}
                  borderColor={
                    answers[q.pk]?.isChanged ? 'blue.500' : 'blue.800'
                  }
                  borderWidth={answers[q.pk]?.isChanged ? 2 : 1}
                />
                <Box mt={5}>
                  <Tippy
                    arrow={true}
                    theme="light"
                    animation="scale"
                    content={comment}
                  >
                    {isTeacher ? (
                      <Button
                        isDisabled={!answers[q.pk]?.pk}
                        onClick={() => {
                          setCommentValue(answers[q.pk]?.comment || '');
                          setSliderValue(answers[q.pk]?.grade || 5);
                          setGradingAnswer(answers[q.pk]?.pk);
                          setMaxPoints(q.max_points);
                          onOpen();
                        }}
                        leftIcon={<LinkIcon />}
                        colorScheme={grade === -1 ? 'blue' : 'yellow'}
                        variant="solid"
                      >
                        {grade === -1
                          ? t('plugin.assestment.ungraded', {
                              maxPoints: q.max_points,
                            })
                          : grade}
                      </Button>
                    ) : (
                      <Tag
                        size={'lg'}
                        variant="subtle"
                        colorScheme={grade === -1 ? 'blue' : 'yellow'}
                      >
                        <TagLeftIcon boxSize="12px" as={LinkIcon} />
                        <TagLabel>
                          {grade === -1
                            ? t('plugin.assestment.ungraded', {
                                maxPoints: q.max_points,
                              })
                            : grade}
                        </TagLabel>
                      </Tag>
                    )}
                  </Tippy>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {!isTeacher && (
        <Button
          mt="20px"
          isLoading={isSaving}
          disabled={!hasChanges}
          colorScheme="blue"
          onClick={handleSaveAll}
        >
          {t('plugin.assestment.saveAll')}
        </Button>
      )}
      <Modal size="lg" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('plugin.assestment.gradeAnswers')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box p={4} pt={6} pb={2}>
              <Slider
                value={sliderValue}
                min={1}
                max={maxPoints}
                step={1}
                aria-label="slider"
                onChange={(val) => setSliderValue(val)}
              >
                {Array.from({ length: 11 }).map((_v, i) => (
                  <SliderMark value={i} mt={2} ml={0} fontSize="sm">
                    {i}
                  </SliderMark>
                ))}
                {sliderValue > 0 && (
                  <SliderMark
                    value={sliderValue}
                    textAlign="center"
                    bg="blue.500"
                    color="white"
                    mt="-10"
                    ml="-5"
                    w="12"
                    borderRadius={8}
                  >
                    {sliderValue}
                  </SliderMark>
                )}
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <Input
                mt={8}
                placeholder={t('plugin.assestment.optionalComment')}
                value={commentValue}
                onChange={(e) => setCommentValue(e.target.value)}
              />
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={grade}
              isLoading={isGradeSaving}
            >
              {t('plugin.assestment.grade')}
            </Button>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('plugin.assestment.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AssessmentPluginBlock;
